import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Margin } from 'styled-components-spacing';
import { Field } from 'components';
import { formatDateTime } from 'helpers/date';
import {
  FileHistoryTable,
  FileHistoryTh,
  FileHistoryTd,
  ClampText,
} from 'shared/ChangeRequest/ChangeRequest.styled';
import { HistoryReason, EmptyNote } from './Application.styled';

/** A rejection is the only status whose reason is a warning. */
const REFUSED = ['REJECTED'];

/**
 * How the application travelled from draft to its current status.
 *
 * It uses the table of the change request screens, so the audit trail of the
 * meetings reads the same way as the one users already know from CIS.
 *
 * Every step names the person, the organisation they acted for and the role
 * that carried the permission behind the change.
 */
const StatusHistory = ({ items }) => {
  const { t } = useTranslation();

  return (
    <Margin top="32">
      <Field id="statusHistory" label={t('page.ma.meetings.application.historyTitle')}>
        {items.length ? (
          <FileHistoryTable>
            <thead>
              <tr>
                <FileHistoryTh>{t('page.ma.meetings.application.historyStatus')}</FileHistoryTh>
                <FileHistoryTh>{t('page.ma.meetings.application.historyAt')}</FileHistoryTh>
                <FileHistoryTh>{t('page.ma.meetings.application.historyBy')}</FileHistoryTh>
                <FileHistoryTh>
                  {t('page.ma.meetings.application.historyOrganization')}
                </FileHistoryTh>
                <FileHistoryTh>{t('page.ma.meetings.application.historyRole')}</FileHistoryTh>
                <FileHistoryTh>{t('page.ma.meetings.application.historyReason')}</FileHistoryTh>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <FileHistoryTd>{item.newStatusLabel}</FileHistoryTd>
                  <FileHistoryTd>{formatDateTime(item.createdAt) || '-'}</FileHistoryTd>
                  <FileHistoryTd>{item.changedBy || '-'}</FileHistoryTd>
                  <FileHistoryTd>{item.changedByOrganization || '-'}</FileHistoryTd>
                  <FileHistoryTd>{item.changedByRole || '-'}</FileHistoryTd>
                  <FileHistoryTd>
                    {item.reason ? (
                      <HistoryReason refused={REFUSED.includes(item.newStatus)}>
                        <ClampText>{item.reason}</ClampText>
                      </HistoryReason>
                    ) : (
                      '-'
                    )}
                  </FileHistoryTd>
                </tr>
              ))}
            </tbody>
          </FileHistoryTable>
        ) : (
          <EmptyNote>{t('page.ma.meetings.application.historyEmpty')}</EmptyNote>
        )}
      </Field>
    </Margin>
  );
};

StatusHistory.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      newStatus: PropTypes.string,
      newStatusLabel: PropTypes.string,
      reason: PropTypes.string,
      changedBy: PropTypes.string,
      changedByOrganization: PropTypes.string,
      changedByRole: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  ),
};

StatusHistory.defaultProps = {
  items: [],
};

export default StatusHistory;
